import { ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Box, Divider, Stack, Typography } from '@mui/material';

import {
  useGetSavedRouteAlertEventsQuery,
  useGetSavedRouteAlertsQuery,
} from 'state/emailAlertsSlices';
import { usePollingIntervalWhenVisible } from 'state/hooks';
import { useGetSavedRoutesQuery } from 'state/savedRouteSlices';

import dayjs from 'dayjs';
import FontAwesomeIcon from 'features/common/FontAwesomeIcon';
import { dateToString } from 'features/common/datetimeUtils';
import { useThrottleValue } from 'features/common/hooks/useThrottleValue';
import LoadingSpinner from 'features/spinner/Spinner';

import { Breadcrumb } from '../Breadcrumb';
import { NotificationsPanel } from '../NotificationsPanel';
import {
  NoAlerts,
  NoNotifications,
  NoResults,
  NoRoutes,
} from '../components/EmptyState';
import { useNotificationProject } from '../useNotificationProject';
import NotificationList from './NotificationList';
import PaginationFooter from './PaginationFooter';
import SearchNav from './SearchNav';
import { Filters } from './types';

import styles from '../NotificationsPanel.module.css';

const PAGE_SIZE = 20;
const PAGE_INITIAL = 1;

type FooterState = {
  pageCount: number;
  allCount: number;
  currCount: number;
};

interface INotificationLog {
  navMode?: boolean;
  todayOnly?: boolean;
  onUnresolvedCountChange?: (v: number) => void;
}

export function NotificationLog({
  navMode,
  todayOnly,
  onUnresolvedCountChange,
}: INotificationLog) {
  const { state } = useLocation() as {
    state?: Partial<Filters>;
  };
  const { projectSlug } = useNotificationProject();

  const { currentData: savedRouteData, isLoading: isLoadingRoutes } =
    useGetSavedRoutesQuery(projectSlug, {
      skip: !projectSlug,
    });
  const { data: alerts, error: alertsError } = useGetSavedRouteAlertsQuery(
    projectSlug,
    {
      skip: !projectSlug,
    },
  );

  const [footer, setFooter] = useState<FooterState>({
    pageCount: 1,
    allCount: 0,
    currCount: 0,
  });
  const [filters, setFilters] = useState<Filters>(() => ({
    searchTerm: undefined, // search term always set to empty string
    resolved: state?.resolved ?? undefined,
    date: todayOnly ? dateToString(dayjs()) : (state?.date ?? undefined),
    routeId: state?.routeId ?? undefined,
  }));

  const throttledSearchTerm = useThrottleValue(filters.searchTerm, 400);
  const filterKey = `${filters.resolved ?? ''}|${filters.date ?? ''}|${throttledSearchTerm ?? ''}`;
  const [pagination, setPagination] = useState(() => ({
    filterKey,
    page: PAGE_INITIAL,
  }));
  // when filters change, set to the first page
  const page =
    pagination.filterKey !== filterKey ? PAGE_INITIAL : pagination.page;
  const setPage = (nextPage: number) => {
    setPagination({ filterKey, page: nextPage });
  };

  const hasFilters = Object.values(filters).some((el) => el !== undefined);
  const shouldPoll = !hasFilters || navMode;

  const visiblePollingInterval = usePollingIntervalWhenVisible(1000 * 60);
  const pollingInterval = shouldPoll ? visiblePollingInterval : 0;
  const {
    currentData,
    isLoading: eventsLoading,
    isFetching: eventsFetching,
    isError: eventsError,
    isUninitialized,
  } = useGetSavedRouteAlertEventsQuery(
    {
      page_size: PAGE_SIZE,
      project_slug: projectSlug,
      page,
      searchTerm: throttledSearchTerm,
      resolved: Number(filters.resolved),
      date: filters.date,
      routeId: filters.routeId,
    },
    {
      skip: Boolean(!projectSlug || alerts?.length === 0),
      pollingInterval,
      refetchOnFocus: navMode,
      refetchOnReconnect: navMode,
    },
  );
  const isLoading =
    eventsLoading ||
    (eventsFetching && (hasFilters || isUninitialized)) ||
    !projectSlug;
  const isError = alertsError || eventsError;
  const notifications = currentData?.results ?? [];

  // the max count displayed in UI will be UNRESOLVED_LIMIT (9+) out of 20 most recent
  const unresolvedCount =
    currentData?.results?.reduce(
      (count, el) => count + (el.ended_on === null ? 1 : 0),
      0,
    ) ?? 0;

  useEffect(() => {
    onUnresolvedCountChange?.(unresolvedCount);
  }, [unresolvedCount]);

  useEffect(() => {
    /** prevents updates of footer info while a new request is in flight */
    if (!eventsFetching && currentData) {
      const count = currentData.count ?? 0;
      const resultsLen = currentData.results?.length ?? 0;
      setFooter({
        pageCount: Math.max(PAGE_INITIAL, Math.ceil(count / PAGE_SIZE)),
        allCount: count,
        currCount: resultsLen,
      });
    }
  }, [eventsFetching, currentData]);

  const updateFilters = (patch: Partial<Filters>) => {
    setFilters((prev) => ({
      ...prev,
      ...patch,
    }));
  };

  let content: ReactNode;
  const hasRoutes = savedRouteData?.savedRoutes?.length > 0;
  const loading = isLoading || isLoadingRoutes;
  if (loading) content = <LoadingSpinner large />;
  else if (isError)
    content = <Typography textAlign="center">Something went wrong</Typography>;
  else if (!hasRoutes) content = <NoRoutes />;
  else if (alerts?.length === 0) content = <NoAlerts />;
  else if (footer.allCount === 0) {
    if (hasFilters && !navMode) content = <NoResults resourceName="events" />;
    else content = <NoNotifications />;
  } else
    content = (
      <NotificationList notifications={notifications} wide={!navMode} />
    );

  if (navMode)
    return (
      <Box
        className={styles.contentInner}
        sx={{ width: !loading ? '100%' : 'auto' }}
      >
        {content}
      </Box>
    );
  return (
    <NotificationsPanel>
      <Stack className={styles.contentWrapper}>
        <Stack className={styles.pageTitle} key={filters?.routeId}>
          <Typography variant="h3" fontWeight={700}>
            All notifications
          </Typography>
          {filters?.routeId && (
            <>
              <FontAwesomeIcon name="chevron-right" />
              <Breadcrumb
                classes={[styles.singleLine]}
                name={state?.searchTerm}
                handleClose={() => updateFilters({ routeId: undefined })}
              />
            </>
          )}
        </Stack>
        <SearchNav filters={filters} onChange={updateFilters} />
        <Divider sx={{ mt: 1.5 }} />
        <Box
          className={styles.contentInner}
          sx={{ width: !loading ? '100%' : 'auto' }}
        >
          {content}
        </Box>
      </Stack>
      <PaginationFooter page={page} setPage={setPage} {...footer} />
    </NotificationsPanel>
  );
}
